import React, { useState, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { FileText, CheckCircle, Calendar, Phone, Download, Users, X, Send, User, Mail, MapPin, GraduationCap, Building, Baby, Heart } from 'lucide-react'
import useAdmissionStore from '../stores/admissionStore'
import { getImportantDates } from '../api/importantDateService'
import logger from '../utils/logger';

const Admissions = () => {
  const { t } = useTranslation()
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  // Get admission store actions
  const addApplication = useAdmissionStore(state => state.addApplication)

  // Important Dates State
  const [importantDates, setImportantDates] = useState([])
  const [loadingDates, setLoadingDates] = useState(true)

  // Fetch important dates from API
  useEffect(() => {
    const fetchImportantDates = async () => {
      try {
        setLoadingDates(true)
        const response = await getImportantDates({ isActive: true })
        if (response.status === 'success' && response.data?.importantDates) {
          setImportantDates(response.data.importantDates)
        }
      } catch (error) {
        logger.error('Error fetching important dates:', error)
        // Fallback to default dates if API fails
        setImportantDates([
          {
            event: "Admission Form Release",
            date: "March 1, 2024",
            status: "completed"
          },
          {
            event: "Last Date for Application",
            date: "April 30, 2024",
            status: "upcoming"
          },
          {
            event: "Document Verification",
            date: "May 1-15, 2024",
            status: "upcoming"
          },
          {
            event: "Admission Results",
            date: "May 20, 2024",
            status: "upcoming"
          }
        ])
      } finally {
        setLoadingDates(false)
      }
    }

    fetchImportantDates()
  }, [])

  // Form Modal State
  const [showAdmissionForm, setShowAdmissionForm] = useState(false)
  const [formStep, setFormStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    // Student Information
    studentName: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    aadharNumber: '',
    
    // Academic Information
    previousSchool: '',
    previousGrade: '',
    classApplying: '',
    academicYear: '2024-25',
    medium: '',
    
    // Parent/Guardian Information
    fatherName: '',
    fatherOccupation: '',
    fatherPhone: '',
    fatherEmail: '',
    motherName: '',
    motherOccupation: '',
    motherPhone: '',
    guardianEmail: '',
    
    // Address Information
    address: '',
    city: '',
    state: 'Gujarat',
    pincode: '',
    
    // Additional Information
    specialNeeds: '',
    previousSchoolResult: '',
    extracurricular: ''
  })
  const [formErrors, setFormErrors] = useState({})

  // Form Validation
  const validateStep = (step) => {
    const errors = {}
    
    if (step === 1) {
      if (!formData.studentName.trim()) errors.studentName = 'Student name is required'
      if (!formData.dateOfBirth) errors.dateOfBirth = 'Date of birth is required'
      if (formData.dateOfBirth && new Date(formData.dateOfBirth) >= new Date()) {
        errors.dateOfBirth = 'Date of birth must be in the past'
      }
      if (!formData.gender) errors.gender = 'Gender is required'
      if (!formData.aadharNumber.trim()) errors.aadharNumber = 'Aadhar number is required'
      if (formData.aadharNumber && !/^\d{12}$/.test(formData.aadharNumber.replace(/\s/g, ''))) {
        errors.aadharNumber = 'Invalid Aadhar number format'
      }
    }
    
    if (step === 2) {
      if (!formData.classApplying) errors.classApplying = 'Class applying for is required'
      if (!formData.medium) errors.medium = 'Medium of instruction is required'
      if (!formData.previousSchool.trim()) errors.previousSchool = 'Previous school is required'
    }
    
    if (step === 3) {
      if (!formData.fatherName.trim()) errors.fatherName = 'Father\'s name is required'
      if (!formData.fatherPhone.trim()) errors.fatherPhone = 'Father\'s phone is required'
      if (formData.fatherPhone && !/^[6-9]\d{9}$/.test(formData.fatherPhone)) {
        errors.fatherPhone = 'Phone must be 10 digits starting with 6-9'
      }
      // Validate fatherEmail only if provided
      if (formData.fatherEmail && formData.fatherEmail.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.fatherEmail)) {
        errors.fatherEmail = 'Invalid email format'
      }
      if (!formData.motherName.trim()) errors.motherName = 'Mother\'s name is required'
      // Validate motherPhone only if provided
      if (formData.motherPhone && formData.motherPhone.trim() !== '' && !/^[6-9]\d{9}$/.test(formData.motherPhone)) {
        errors.motherPhone = 'Phone must be 10 digits starting with 6-9'
      }
      // Validate guardianEmail only if provided
      if (formData.guardianEmail && formData.guardianEmail.trim() !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guardianEmail)) {
        errors.guardianEmail = 'Invalid email format'
      }
    }
    
    if (step === 4) {
      if (!formData.address.trim()) errors.address = 'Address is required'
      if (!formData.city.trim()) errors.city = 'City is required'
      if (!formData.state.trim()) errors.state = 'State is required'
      if (!formData.pincode.trim()) errors.pincode = 'Pincode is required'
      if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) {
        errors.pincode = 'Invalid pincode format'
      }
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Form Handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleNextStep = () => {
    if (validateStep(formStep)) {
      setFormStep(prev => Math.min(prev + 1, 4))
    }
  }

  const handlePrevStep = () => {
    setFormStep(prev => Math.max(prev - 1, 1))
  }

  const handleFormSubmit = async () => {
    if (!validateStep(4)) return
    
    setIsSubmitting(true)
    try {
      // Transform form data to match the backend expected format
      // No need for gradeMapping anymore since we're using the exact backend enum values
      
      const applicationData = {
        // Student Information (required)
        studentName: formData.studentName,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        bloodGroup: formData.bloodGroup,
        aadharNumber: formData.aadharNumber.replace(/\s/g, ''), // Remove all spaces
        
        // Academic Information (required)
        gradeAppliedFor: formData.classApplying, // Use the exact value from dropdown
        previousSchool: formData.previousSchool,
        previousGrade: formData.previousGrade || undefined,
        academicYear: formData.academicYear,
        
        // Parent Information (required)
        fatherName: formData.fatherName,
        fatherOccupation: formData.fatherOccupation,
        fatherPhone: formData.fatherPhone,
        fatherEmail: formData.fatherEmail && formData.fatherEmail.trim() !== '' ? formData.fatherEmail : undefined,
        motherName: formData.motherName,
        motherOccupation: formData.motherOccupation,
        motherPhone: formData.motherPhone && formData.motherPhone.trim() !== '' ? formData.motherPhone : undefined,
        
        // Address Information (required as nested object)
        address: {
          street: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode
        }
      }

      // Debug: Log the data being sent
      logger.log('Submitting admission data:', applicationData)
      logger.log('Full application data:', JSON.stringify(applicationData, null, 2))

      // Call the store action which calls the backend API
      const result = await addApplication(applicationData)
      
      logger.log('Submission result:', result)
      
      if (result.success) {
        // Show success message
        alert(`Admission form submitted successfully! 
Application Number: ${result.data.applicationNumber || 'Pending'}
We will contact you within 2-3 working days.`)
        
        // Reset form
        setShowAdmissionForm(false)
        setFormStep(1)
        setFormData({
          studentName: '', dateOfBirth: '', gender: '', bloodGroup: '', aadharNumber: '',
          previousSchool: '', previousGrade: '', classApplying: '', academicYear: '2024-25', medium: '',
          fatherName: '', fatherOccupation: '', fatherPhone: '', fatherEmail: '', motherName: '', motherOccupation: '', motherPhone: '', guardianEmail: '',
          address: '', city: '', state: 'Gujarat', pincode: '',
          specialNeeds: '', previousSchoolResult: '', extracurricular: ''
        })
        setFormErrors({})
      } else {
        // Show error message
        alert(`Error: ${result.message || 'Failed to submit application. Please try again.'}`)
      }
    } catch (error) {
      logger.error('Form submission error:', error)
      alert('Error submitting form. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const admissionSteps = [
    {
      step: 1,
      icon: <FileText className="w-8 h-8" />,
      title: t('admissions.steps.applicationForm'),
      description: t('admissions.steps.applicationFormDesc')
    },
    {
      step: 2,
      icon: <CheckCircle className="w-8 h-8" />,
      title: t('admissions.steps.documentVerification'),
      description: t('admissions.steps.documentVerificationDesc')
    },
    {
      step: 3,
      icon: <Users className="w-8 h-8" />,
      title: t('admissions.steps.interaction'),
      description: t('admissions.steps.interactionDesc')
    },
    {
      step: 4,
      icon: <CheckCircle className="w-8 h-8" />,
      title: t('admissions.steps.admissionConfirmation'),
      description: t('admissions.steps.admissionConfirmationDesc')
    }
  ]

  const requiredDocuments = [
    t('admissions.documents.birthCertificate'),
    t('admissions.documents.transferCertificate'),
    t('admissions.documents.academicRecords'),
    t('admissions.documents.aadharCard'),
    t('admissions.documents.photographs'),
    t('admissions.documents.casteCertificate'),
    t('admissions.documents.incomeCertificate')
  ]

  // State for fee structure toggle
  const [selectedFeeType, setSelectedFeeType] = useState('boys')

  const feeStructureBoys = [
    {
      level: "STD 5",
      schoolFee: "₹4,000",
      hostelFee: "₹12,500",
      total: "₹16,500",
      yearly: "₹33,000"
    },
    {
      level: "STD 6",
      schoolFee: "₹5,000",
      hostelFee: "₹12,500",
      total: "₹17,500",
      yearly: "₹35,000"
    },
    {
      level: "STD 7",
      schoolFee: "₹5,000",
      hostelFee: "₹12,500",
      total: "₹17,500",
      yearly: "₹35,000"
    },
    {
      level: "STD 8",
      schoolFee: "₹6,000",
      hostelFee: "₹15,000",
      total: "₹21,000",
      yearly: "₹42,000"
    },
    {
      level: "STD 9",
      schoolFee: "₹8,000",
      hostelFee: "₹15,000",
      total: "₹21,000",
      yearly: "₹42,000"
    },
    {
      level: "STD 10",
      schoolFee: "₹8,000",
      hostelFee: "₹17,500",
      total: "₹25,500",
      yearly: "₹51,000"
    },
    {
      level: "11-12 Com",
      schoolFee: "₹9,000",
      hostelFee: "₹18,000",
      total: "₹27,500",
      yearly: "₹55,000"
    },
    {
      level: "11-12 Sci",
      schoolFee: "₹18,000",
      hostelFee: "₹18,000",
      total: "₹36,000",
      yearly: "₹72,000"
    }
  ]

  const feeStructureGirls = [
    {
      level: "STD 5",
      schoolFee: "₹4,000",
      hostelFee: "₹10,500",
      total: "₹14,500",
      yearly: "₹29,000"
    },
    {
      level: "STD 6",
      schoolFee: "₹5,000",
      hostelFee: "₹10,500",
      total: "₹15,500",
      yearly: "₹31,000"
    },
    {
      level: "STD 7",
      schoolFee: "₹5,000",
      hostelFee: "₹10,500",
      total: "₹15,500",
      yearly: "₹31,000"
    },
    {
      level: "STD 8",
      schoolFee: "₹6,000",
      hostelFee: "₹11,500",
      total: "₹17,500",
      yearly: "₹35,000"
    },
    {
      level: "STD 9",
      schoolFee: "₹6,000",
      hostelFee: "₹11,500",
      total: "₹17,500",
      yearly: "₹35,000"
    },
    {
      level: "STD 10",
      schoolFee: "₹8,000",
      hostelFee: "₹14,500",
      total: "₹22,500",
      yearly: "₹45,000"
    },
    {
      level: "11-12 Com",
      schoolFee: "₹9,500",
      hostelFee: "₹14,500",
      total: "₹24,000",
      yearly: "₹48,000"
    },
    {
      level: "11-12 Sci",
      schoolFee: "₹18,000",
      hostelFee: "₹14,500",
      total: "₹32,500",
      yearly: "₹65,000"
    }
  ]

  const currentFeeStructure = selectedFeeType === 'boys' ? feeStructureBoys : feeStructureGirls

  return (
    <section id="admissions" className="py-10 sm:py-12 md:py-16 lg:py-20 xl:py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div 
          ref={ref}
          className={`transition-all duration-1000 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Section Header */}
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-primary-dark font-poppins mb-3 sm:mb-4">
              {t('admissions.title')}
            </h2>
            <div className="w-16 sm:w-20 lg:w-24 h-1 bg-primary-yellow mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto px-2">
              {t('admissions.description')}
            </p>
          </div>

          {/* Admission Process */}
          <div className="mb-10 sm:mb-12 lg:mb-16">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center text-primary-dark mb-8 sm:mb-10 lg:mb-12 font-montserrat">
              {t('admissions.process')}
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {admissionSteps.map((step, index) => (
                <div key={index} className="text-center">
                  {/* Step Number */}
                  <div className="relative mb-4 sm:mb-6">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-primary-dark rounded-full flex items-center justify-center text-white font-bold text-lg sm:text-xl mx-auto mb-3 sm:mb-4">
                      {step.step}
                    </div>
                    {index < admissionSteps.length - 1 && (
                      <div className="hidden lg:block absolute top-6 sm:top-7 lg:top-8 left-full w-full h-0.5 bg-primary-dark/30"></div>
                    )}
                  </div>
                  
                  {/* Icon */}
                  <div className="text-primary-dark mb-3 sm:mb-4 flex justify-center">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8">{step.icon}</div>
                  </div>
                  
                  {/* Content */}
                  <h4 className="text-base sm:text-lg font-semibold text-primary-dark mb-2 sm:mb-3">
                    {step.title}
                  </h4>
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed px-2">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 mb-10 sm:mb-12 lg:mb-16">
            {/* Required Documents */}
            <div className="bg-gradient-to-br from-primary-maroon/5 to-primary-blue/5 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-8">
              <h3 className="text-lg sm:text-xl font-bold text-primary-maroon mb-4 sm:mb-6 flex items-center">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
                {t('admissions.documents.title')}
              </h3>
              <div className="space-y-2 sm:space-y-3">
                {requiredDocuments.map((doc, index) => (
                  <div key={index} className="flex items-center space-x-2 sm:space-x-3">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary-green flex-shrink-0" />
                    <span className="text-gray-700 text-sm sm:text-base">{doc}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 sm:mt-8">
                <button 
                  onClick={() => setShowAdmissionForm(true)}
                  className="btn-primary w-full flex items-center justify-center space-x-2 py-3 sm:py-4 text-sm sm:text-base touch-target"
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Fill Admission Form</span>
                </button>
              </div>
            </div>

            {/* Important Dates */}
            <div className="bg-gradient-to-br from-primary-green/5 to-primary-yellow/5 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-8">
              <h3 className="text-lg sm:text-xl font-bold text-primary-maroon mb-4 sm:mb-6 flex items-center">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
                {t('admissions.importantDates.title')}
              </h3>
              <div className="space-y-3 sm:space-y-4">
                {importantDates.map((date, index) => (
                  <div key={index} className="flex items-center justify-between p-3 sm:p-4 bg-white rounded-lg shadow-sm gap-2">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-gray-800 text-sm sm:text-base truncate">{date.event}</h4>
                      <p className="text-gray-600 text-xs sm:text-sm">{date.date}</p>
                    </div>
                    <div className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0 ${
                      date.status === 'completed' 
                        ? 'bg-primary-green/20 text-primary-green' 
                        : 'bg-primary-yellow/20 text-primary-brown'
                    }`}>
                      {date.status === 'completed' ? t('admissions.importantDates.completed') : t('admissions.importantDates.upcoming')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>



          {/* Contact Information */}
          <div className="bg-gradient-to-r from-primary-dark to-primary-mid rounded-xl sm:rounded-2xl p-5 sm:p-8 lg:p-12 text-white text-center">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6">
              {t('admissions.feeStructure.admissionHelp.title')}
            </h3>
            <p className="text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 opacity-90 px-2">
              {t('admissions.feeStructure.admissionHelp.subtitle')}
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="bg-white/10 rounded-lg p-4 sm:p-6">
                <Phone className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 sm:mb-3" />
                <h4 className="font-semibold mb-1 sm:mb-2 text-sm sm:text-base">{t('admissions.feeStructure.admissionHelp.callUs')}</h4>
                <a href="tel:+919427805272" className="text-sm sm:text-base hover:underline">+91 94278 05272</a>
              </div>
              <div className="bg-white/10 rounded-lg p-4 sm:p-6">
                <FileText className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 sm:mb-3" />
                <h4 className="font-semibold mb-1 sm:mb-2 text-sm sm:text-base">{t('admissions.feeStructure.admissionHelp.email')}</h4>
                <p className="text-xs sm:text-sm break-all">info@sardarpateleducationtrust.org</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
                <Calendar className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 sm:mb-3" />
                <h4 className="font-semibold mb-1 sm:mb-2 text-sm sm:text-base">{t('admissions.feeStructure.admissionHelp.admissionHelpline')}</h4>
                <a href="tel:+919427805272" className="text-sm sm:text-base hover:underline">+91 94278 05272</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admission Form Modal */}
      {showAdmissionForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto my-2 sm:my-4">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-primary-dark">Admission Application Form</h3>
                <p className="text-gray-600 mt-1 text-sm">Step {formStep} of 4</p>
              </div>
              <button 
                onClick={() => setShowAdmissionForm(false)}
                className="text-gray-500 hover:text-gray-700 p-2 touch-target"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="px-4 sm:px-6 py-3 sm:py-4">
              <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                <div 
                  className="bg-primary-dark h-1.5 sm:h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(formStep / 4) * 100}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs sm:text-sm text-gray-600">
                <span className="truncate">Student</span>
                <span className="truncate">Academic</span>
                <span className="truncate">Parents</span>
                <span className="truncate">Address</span>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-4 sm:p-6">
              {/* Step 1: Student Information */}
              {formStep === 1 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-primary-maroon mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Student Information
                  </h4>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Full Name *</label>
                      <input
                        type="text"
                        value={formData.studentName}
                        onChange={(e) => handleInputChange('studentName', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.studentName ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter student's full name"
                      />
                      {formErrors.studentName && <p className="text-red-500 text-sm mt-1">{formErrors.studentName}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Date of Birth *</label>
                      <input
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.dateOfBirth ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {formErrors.dateOfBirth && <p className="text-red-500 text-sm mt-1">{formErrors.dateOfBirth}</p>}
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Gender *</label>
                      <select
                        value={formData.gender}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.gender ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                      {formErrors.gender && <p className="text-red-500 text-sm mt-1">{formErrors.gender}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Blood Group</label>
                      <select
                        value={formData.bloodGroup}
                        onChange={(e) => handleInputChange('bloodGroup', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent"
                      >
                        <option value="">Select Blood Group</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Aadhar Number *</label>
                    <input
                      type="text"
                      value={formData.aadharNumber}
                      onChange={(e) => handleInputChange('aadharNumber', e.target.value.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 '))}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.aadharNumber ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="XXXX XXXX XXXX"
                      maxLength="14"
                    />
                    {formErrors.aadharNumber && <p className="text-red-500 text-sm mt-1">{formErrors.aadharNumber}</p>}
                  </div>
                </div>
              )}

              {/* Step 2: Academic Information */}
              {formStep === 2 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-primary-maroon mb-4 flex items-center">
                    <GraduationCap className="w-5 h-5 mr-2" />
                    Academic Information
                  </h4>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Class Applying For *</label>
                      <select
                        value={formData.classApplying}
                        onChange={(e) => handleInputChange('classApplying', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.classApplying ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">Select Class</option>
                        <option value="K.G. to Std 8th">K.G. to Std 8th</option>
                        <option value="9th to 12th">9th to 12th</option>
                        <option value="B.B.A.">B.B.A.</option>
                        <option value="B.B.A. (Honours)">B.B.A. (Honours)</option>
                        <option value="B.COM.">B.COM.</option>
                        <option value="B.COM. (Honours)">B.COM. (Honours)</option>
                        <option value="B.C.A.">B.C.A.</option>
                        <option value="B.C.A. (Honours)">B.C.A. (Honours)</option>
                        <option value="B.SC.">B.SC.</option>
                        <option value="B.SC. (Honours)">B.SC. (Honours)</option>
                        <option value="B.A.">B.A.</option>
                        <option value="B.A. (Honours)">B.A. (Honours)</option>
                        <option value="P.T.C.">P.T.C.</option>
                        <option value="B.ED.">B.ED.</option>
                        <option value="LL.B.">LL.B.</option>
                        <option value="M.A.">M.A.</option>
                        <option value="M.COM.">M.COM.</option>
                      </select>
                      {formErrors.classApplying && <p className="text-red-500 text-sm mt-1">{formErrors.classApplying}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Academic Year</label>
                      <input
                        type="text"
                        value={formData.academicYear}
                        onChange={(e) => handleInputChange('academicYear', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent"
                        placeholder="2024-25"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Medium of Instruction *</label>
                    <select
                      value={formData.medium}
                      onChange={(e) => handleInputChange('medium', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.medium ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="">Select Medium</option>
                      <option value="english">English</option>
                      <option value="gujarati">Gujarati</option>
                    </select>
                    {formErrors.medium && <p className="text-red-500 text-sm mt-1">{formErrors.medium}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Previous School *</label>
                    <input
                      type="text"
                      value={formData.previousSchool}
                      onChange={(e) => handleInputChange('previousSchool', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.previousSchool ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter previous school name (or 'First Time' if new admission)"
                    />
                    {formErrors.previousSchool && <p className="text-red-500 text-sm mt-1">{formErrors.previousSchool}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Previous Grade/Class</label>
                    <input
                      type="text"
                      value={formData.previousGrade}
                      onChange={(e) => handleInputChange('previousGrade', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent"
                      placeholder="Enter previous grade/class (or 'N/A' if not applicable)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Previous Academic Result</label>
                    <input
                      type="text"
                      value={formData.previousSchoolResult}
                      onChange={(e) => handleInputChange('previousSchoolResult', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent"
                      placeholder="Enter percentage/grade (if applicable)"
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Parent/Guardian Information */}
              {formStep === 3 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-primary-maroon mb-4 flex items-center">
                    <Heart className="w-5 h-5 mr-2" />
                    Parent/Guardian Information
                  </h4>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Father's Name *</label>
                      <input
                        type="text"
                        value={formData.fatherName}
                        onChange={(e) => handleInputChange('fatherName', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.fatherName ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter father's full name"
                      />
                      {formErrors.fatherName && <p className="text-red-500 text-sm mt-1">{formErrors.fatherName}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Father's Occupation</label>
                      <input
                        type="text"
                        value={formData.fatherOccupation}
                        onChange={(e) => handleInputChange('fatherOccupation', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent"
                        placeholder="Enter father's occupation"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Father's Phone Number *</label>
                    <input
                      type="tel"
                      value={formData.fatherPhone}
                      onChange={(e) => handleInputChange('fatherPhone', e.target.value.replace(/\D/g, ''))}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.fatherPhone ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter 10-digit phone number"
                      maxLength="10"
                    />
                    {formErrors.fatherPhone && <p className="text-red-500 text-sm mt-1">{formErrors.fatherPhone}</p>}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Father's Email Address</label>
                    <input
                      type="email"
                      value={formData.fatherEmail}
                      onChange={(e) => handleInputChange('fatherEmail', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.fatherEmail ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter father's email (optional)"
                    />
                    {formErrors.fatherEmail && <p className="text-red-500 text-sm mt-1">{formErrors.fatherEmail}</p>}
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Mother's Name *</label>
                      <input
                        type="text"
                        value={formData.motherName}
                        onChange={(e) => handleInputChange('motherName', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.motherName ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter mother's full name"
                      />
                      {formErrors.motherName && <p className="text-red-500 text-sm mt-1">{formErrors.motherName}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Mother's Occupation</label>
                      <input
                        type="text"
                        value={formData.motherOccupation}
                        onChange={(e) => handleInputChange('motherOccupation', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent"
                        placeholder="Enter mother's occupation"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Mother's Phone Number</label>
                      <input
                        type="tel"
                        value={formData.motherPhone}
                        onChange={(e) => handleInputChange('motherPhone', e.target.value.replace(/\D/g, ''))}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.motherPhone ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter 10-digit phone number"
                        maxLength="10"
                      />
                      {formErrors.motherPhone && <p className="text-red-500 text-sm mt-1">{formErrors.motherPhone}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Email Address</label>
                      <input
                        type="email"
                        value={formData.guardianEmail}
                        onChange={(e) => handleInputChange('guardianEmail', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.guardianEmail ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter email address (optional)"
                      />
                      {formErrors.guardianEmail && <p className="text-red-500 text-sm mt-1">{formErrors.guardianEmail}</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Address & Additional Information */}
              {formStep === 4 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-primary-maroon mb-4 flex items-center">
                    <MapPin className="w-5 h-5 mr-2" />
                    Address & Additional Information
                  </h4>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Complete Address *</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.address ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter complete address"
                      rows="3"
                    />
                    {formErrors.address && <p className="text-red-500 text-sm mt-1">{formErrors.address}</p>}
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">City *</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.city ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter city"
                      />
                      {formErrors.city && <p className="text-red-500 text-sm mt-1">{formErrors.city}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">State *</label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.state ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter state"
                      />
                      {formErrors.state && <p className="text-red-500 text-sm mt-1">{formErrors.state}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Pincode *</label>
                      <input
                        type="text"
                        value={formData.pincode}
                        onChange={(e) => handleInputChange('pincode', e.target.value.replace(/\D/g, ''))}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent ${formErrors.pincode ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter pincode"
                        maxLength="6"
                      />
                      {formErrors.pincode && <p className="text-red-500 text-sm mt-1">{formErrors.pincode}</p>}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Special Needs/Medical Conditions</label>
                    <textarea
                      value={formData.specialNeeds}
                      onChange={(e) => handleInputChange('specialNeeds', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent"
                      placeholder="Please mention any special needs, allergies, or medical conditions"
                      rows="2"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Extracurricular Interests</label>
                    <textarea
                      value={formData.extracurricular}
                      onChange={(e) => handleInputChange('extracurricular', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-maroon focus:border-transparent"
                      placeholder="Student's interests in sports, arts, music, etc."
                      rows="2"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between items-center p-6 border-t border-gray-200">
              <button
                onClick={handlePrevStep}
                disabled={formStep === 1}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="flex space-x-3">
                {formStep < 4 ? (
                  <button
                    onClick={handleNextStep}
                    className="btn-secondary"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={handleFormSubmit}
                    disabled={isSubmitting}
                    className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Submit Application</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default Admissions

